import React from 'react';

import { Button } from '@/components/shared/buttons/button';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { Section } from '@/components/shared/layout/section';
import { Image } from '@/components/shared/media/image';
import { generateSplashPageImageSources } from '@/components/shared/media/image/generate-sources';
import { useInViewRef } from '@/components/shared/utility/hooks';
import { styled } from '@/stitches.config';
import {
  IItem,
  IMy24Model,
  IMy24ProductMenu,
  IMy24Tracking,
} from '@/types/shared';

const animation = {
  transform: 'translate(0, 0)',
  opacity: 1,
  transitionProperty: 'transform, opacity',
  transitionTimingFunction: 'cubic-bezier(0.33, 1, 0.68, 1)',
};

const SectionStyled = styled(Section, {
  '@supports (height: 100vh)': {
    height: '100vh',
  },
  '@supports (height: 100svh)': {
    height: '100svh',
  },
  '@lg': {
    minHeight: 480,
  },
});

const LogoStyled = styled(Box, {
  width:
    'clamp(192px, 192px + (320 - 192) * ((100vw - 360px) / (1024 - 360)), 320px)',
  variants: {
    show: {
      false: {
        opacity: 0,
        transform: 'translate(0, 30px)',
      },
      true: {
        ...animation,
        transitionDelay: '0.4s',
        transitionDuration: '0.8s',
      },
    },
  },
});

const ContentWrapperStyled = styled(Box, {
  // mobile
  flexDirection: 'column',
  alignItems: 'stretch',
  textAlign: 'center',
  gap: '$space-10',
  '@media (min-width: 480px) and (max-width: 1023px) and (orientation: landscape)':
    {
      // desktop
      flexDirection: 'row',
      alignItems: 'flex-end',
      textAlign: 'left',
    },

  '@media (min-width: 1024px)': {
    // desktop
    flexDirection: 'row',
    alignItems: 'flex-end',
    textAlign: 'left',
  },
  '@media (min-width: 1024px) and (max-width: 1439px) and (orientation: portrait)':
    {
      // mobile
      flexDirection: 'column',
      alignItems: 'stretch',
      textAlign: 'center',
    },
});

const ContentStyled = styled(Box, {
  alignItems: 'center',
  '@media (min-width: 480px) and (max-width: 1023px) and (orientation: landscape)':
    {
      // desktop
      alignItems: 'flex-start',
    },
  '@media (min-width: 1024px)': {
    // desktop
    alignItems: 'flex-start',
  },
  '@media (min-width: 1024px) and (max-width: 1439px) and (orientation: portrait)':
    {
      // mobile
      alignItems: 'center',
    },
  '> h1': {
    maxWidth: '$size-144',
  },
  variants: {
    show: {
      false: {
        '> h1': {
          opacity: 0,
          transform: 'translate(0, 30px)',
        },
      },
      true: {
        '> h1': {
          ...animation,
          transitionDelay: '0.6s',
          transitionDuration: '1s',
        },
      },
    },
  },
});

const ButtonWrapperStyled = styled(Box, {
  // mobile
  marginTop: '$space-auto',
  minWidth: '$size-56',
  '@media (min-width: 480px) and (max-width: 1023px) and (orientation: landscape)':
    {
      // desktop
      marginTop: '$space-0',
    },
  '@media (min-width: 768px) and (max-width: 1023px) and (orientation: portrait)':
    {
      // mobile
      marginTop: '$space-auto',
    },

  '@media (min-width: 1024px)': {
    // desktop
    marginTop: '$space-0',
  },
  '@media (min-width: 1024px) and (max-width: 1439px) and (orientation: portrait)':
    {
      // mobile
      marginTop: '$space-auto',
    },
  variants: {
    show: {
      false: {
        '> *': {
          opacity: 0,
          transform: 'translate(0, 30px)',
        },
      },
      true: {
        '> *': {
          ...animation,
          transitionDelay: '0.8s',
          transitionDuration: '1.2s',
        },
      },
    },
  },
});

export interface ProductHeroBannerProps {
  active?: boolean;
  menu?: IMy24ProductMenu;
  model?: IMy24Model;
  item?: IItem;
  dataTrack?: IMy24Tracking;
  gradientColor?: string;
  dataTest?: string;
}

export const ProductHeroBanner = ({
  active = false,
  menu,
  model,
  item,
  dataTrack = 'lcv-product',
  gradientColor,
  dataTest = 'banner',
}: ProductHeroBannerProps) => {
  const hash = menu?.hash || '';
  const [viewRef, inView] = useInViewRef({ rootMargin: '0px 0px -30% 0px' });

  if (!active) return null;

  return (
    <SectionStyled
      ref={viewRef as React.Ref<HTMLDivElement>}
      className="h-screen"
      backgroundColor="black"
      data-impression="section-banner"
      data-hash={hash}
      test="section_banner"
      css={{
        paddingTop: '$space-16',
        '@lg': {
          paddingTop: 'var(--desktop-transparent-nav-height)',
        },
      }}
    >
      {!!item?.image?.src && (
        <Image
          priority
          src={item?.image?.src}
          alt={item?.image?.alt ? item?.image?.alt : (model as string)}
          width={600}
          height={800}
          layout="fill"
          sources={generateSplashPageImageSources({
            mobileSrc: item?.image?.src,
            mobileOptions: {
              quality: 80,
            },
            desktopSrc: item?.imageDesktop?.src,
            desktopOptions: {
              quality: 80,
            },
          })}
        />
      )}
      <Box
        position="absolute"
        inset="0"
        pointerEvents="none"
        css={{
          '&::before, &::after': {
            content: '""',
            position: 'absolute',
            inset: '$space-0',
          },
          ...(gradientColor && {
            '&::before': {
              background: `linear-gradient(180deg, rgba(${gradientColor}, 1) 0%, rgba(0, 0, 0, 0) 33.33%)`,
              '@lg': {
                background: `linear-gradient(180deg, rgba(${gradientColor}, 0.5) 0%, rgba(0, 0, 0, 0) 33.33%)`,
              },
            },
          }),
          '&::after': {
            background:
              'linear-gradient(180deg, rgba(0, 0, 0, 0) 66.67%, rgba(0, 0, 0, 1) 100%)',
          },
        }}
      />
      <Section as="div" size="2xl" css={{ height: '100%' }}>
        <Container height="full">
          <ContentWrapperStyled
            position="relative"
            display="flex"
            height="full"
          >
            <ContentStyled
              show={inView}
              width="full"
              display="flex"
              direction="column"
              marginX="auto"
              gap="4"
            >
              {!!item?.logo?.src && (
                <LogoStyled position="relative" fontSize="0" show={inView}>
                  <Image
                    priority
                    src={item?.logo?.src}
                    alt={item?.logo?.alt}
                    width={item?.logo?.width}
                    height={item?.logo?.height}
                    layout="responsive"
                    objectFit="contain"
                    sources={[
                      {
                        srcset: item?.logo?.src,
                        media: { minWidth: 768 },
                        options: {
                          width: 320,
                        },
                      },
                      {
                        srcset: item?.logo?.src,
                        media: { maxWidth: 767 },
                        options: {
                          width: 192,
                        },
                      },
                    ]}
                    css={{
                      // mobile
                      '& img': {
                        objectPosition: 'center !important',
                      },
                      '@media (min-width: 480px) and (max-width: 1023px) and (orientation: landscape)':
                        {
                          // desktop
                          '& img': {
                            objectPosition: 'left !important',
                          },
                        },

                      '@media (min-width: 1024px)': {
                        // desktop
                        '& img': {
                          objectPosition: 'left !important',
                        },
                      },
                      '@media (min-width: 1024px) and (max-width: 1439px) and (orientation: portrait)':
                        {
                          // mobile
                          '& img': {
                            objectPosition: 'center !important',
                          },
                        },
                    }}
                  />
                </LogoStyled>
              )}

              {!!item?.description && (
                <Text
                  as="h1"
                  variant="text-white"
                  size="text-sub-h2"
                  font="bold"
                  wrap
                  dangerouslySetInnerHTML={{
                    __html: item?.description,
                  }}
                  css={{
                    '@maxmd': { '@portrait': { textAlign: 'center' } },
                  }}
                />
              )}
            </ContentStyled>
            <ButtonWrapperStyled show={inView} data-myi-hide>
              {item?.buttons?.map((button, index) => (
                <Button
                  key={index}
                  href={button?.url}
                  variant="button-white"
                  label={button?.label}
                  border={button?.border}
                  isMinWidth={button?.isMinWidth}
                  tracking={{
                    dataTrack: dataTrack,
                    dataTrackSection: 'banner',
                    dataTrackText: button?.label,
                    dataTrackUrl: button?.url,
                  }}
                  test={{
                    dataTest: `${dataTest}_ctaButton_${index + 1}`,
                  }}
                />
              ))}
            </ButtonWrapperStyled>
          </ContentWrapperStyled>
        </Container>
      </Section>
    </SectionStyled>
  );
};

ProductHeroBanner.displayName = 'ProductHeroBanner';
